// Sticky three-phase progress strip for the Duo/Trio customer journey.
//
// Spec: docs/superpowers/specs/2026-06-04-multi-album-flow-design.md
//
// PROBLEM THIS SOLVES
// 100% of Duo customers (9/9 in prod) dropped off after album 1. Root cause
// was no advance warning of how much was ahead — they hit "Confirm" on album
// 1 expecting checkout and got ambushed by album 2's design picker. This
// strip is the anti-surprise: it's pinned to the top of every step from
// cover/1 through checkout, so the customer always knows the shape of the
// journey from the first second.
//
// DESIGN
//   - Three phase chips, horizontally aligned, gold dividers between.
//   - Phase 1 ("Vos albums") contains 2*N dots inside it — one for each
//     (cover, upload) pair across all N albums.
//   - Phase 2 ("Création") is a single chip; sub-state is communicated via
//     the icon: ✨ idle / ⏳ generating / ✏️ manual edit.
//   - Phase 3 ("Finalisation") is the single PDF-and-checkout chip.
//   - Active dot/chip = gold gradient with a soft glow.
//   - Done dot/chip = solid green with a check mark.
//   - Upcoming = muted ghost outline at 18 % opacity (per ui-ux-pro-max:
//     "quiet enough to ignore until needed").
//   - Sub-label under the active phase tells the customer EXACTLY what
//     they're doing in concrete language ("Couverture · Bali", not "Step 3").

import { memo } from 'react';

const C = {
  gold:      '#C9A24A',
  goldDeep:  '#A8842F',
  goldLight: '#D4B86A',
  dark:      '#1F2937',
  muted:     '#6B7280',
  white:     '#FFFFFF',
  success:   '#10B981',
  beige:     '#F5EFE0',
};

export type MultiAlbumPhase = 1 | 2 | 3;

export interface MultiAlbumProgressProps {
  /** Duo = 2, Trio = 3. Sets the number of dots inside Phase 1. */
  totalAlbums: number;
  /** Which phase the customer is currently in. */
  currentPhase: MultiAlbumPhase;
  /** Within Phase 1, which of the 2*N sub-steps is active (1-indexed).
   *  Ignored when currentPhase !== 1. */
  phase1Step?: number;
  /** Within Phase 2, what's the customer doing right now. Used to swap the
   *  ✨ icon for ⏳ (generating) or ✏️ (manual editor open). */
  phase2State?: 'mode' | 'ai-generating' | 'ai-preview' | 'manual-edit';
  /** Optional sub-label shown UNDER the active phase chip. Free-text so the
   *  parent can pass "Couverture · Bali" or "Album 2 sur 2" — whatever
   *  reads more concretely for the current sub-step. */
  subLabel?: string;
}

function PhaseChip({
  active,
  done,
  label,
  icon,
}: {
  active: boolean;
  done: boolean;
  label: string;
  icon: React.ReactNode;
}) {
  const bg = done
    ? C.success
    : active
      ? `linear-gradient(135deg, ${C.gold}, ${C.goldDeep})`
      : `${C.gold}1f`;
  const color = done || active ? C.white : C.muted;
  return (
    <div
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide shrink-0 transition-all"
      style={{
        background: bg,
        color,
        boxShadow: active ? `0 4px 12px ${C.gold}55` : 'none',
        animation: active ? 'storizMapulse 2.4s ease-in-out infinite' : undefined,
      }}
    >
      <span className="inline-flex items-center justify-center w-3.5 h-3.5">{icon}</span>
      <span>{label}</span>
    </div>
  );
}

function Phase1Dots({
  totalAlbums,
  active,
  done,
  currentStep,
}: {
  totalAlbums: number;
  active: boolean;
  done: boolean;
  /** 1-indexed; 0 = none done yet. */
  currentStep: number;
}) {
  // 2 sub-steps per album: cover + upload.
  const count = totalAlbums * 2;
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: count }).map((_, i) => {
        const idx = i + 1;
        const dotDone = done || idx < currentStep;
        const dotActive = active && idx === currentStep;
        const bg = dotDone
          ? C.success
          : dotActive
            ? C.gold
            : `${C.gold}33`;
        return (
          <span
            key={i}
            className="rounded-full transition-all"
            style={{
              width: dotActive ? 10 : 7,
              height: dotActive ? 10 : 7,
              background: bg,
              boxShadow: dotActive ? `0 0 0 3px ${C.gold}25` : 'none',
            }}
          />
        );
      })}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}
function SparkIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2zm7 10l1 2.5 2.5 1-2.5 1L19 19l-1-2.5L15.5 15.5l2.5-1L19 12z" />
    </svg>
  );
}
function HourglassIcon() {
  return (
    <svg className="animate-spin" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-3-6.7" />
    </svg>
  );
}
function PencilIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}
function PackageIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
    </svg>
  );
}

export const MultiAlbumProgress = memo(function MultiAlbumProgress({
  totalAlbums,
  currentPhase,
  phase1Step = 1,
  phase2State = 'mode',
  subLabel,
}: MultiAlbumProgressProps) {
  const phase2Icon = phase2State === 'ai-generating'
    ? <HourglassIcon />
    : phase2State === 'manual-edit'
      ? <PencilIcon />
      : <SparkIcon />;

  return (
    <div
      className="sticky top-0 z-30 w-full"
      style={{
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${C.gold}20`,
        paddingTop: 'env(safe-area-inset-top)',
      }}
      role="navigation"
      aria-label="Progression de votre commande"
    >
      <div className="max-w-5xl mx-auto px-4 py-2.5 flex flex-col gap-1.5">
        {/* Phase chips row */}
        <div className="flex items-center gap-2 overflow-x-auto storiz-strip-no-native-bar">
          {/* Phase 1: title chip + dots side-by-side so the dots ARE the progress */}
          <div className="inline-flex items-center gap-2 shrink-0">
            <PhaseChip
              active={currentPhase === 1}
              done={currentPhase > 1}
              label="Vos albums"
              icon={currentPhase > 1 ? <CheckIcon /> : <SparkIcon />}
            />
            <Phase1Dots
              totalAlbums={totalAlbums}
              active={currentPhase === 1}
              done={currentPhase > 1}
              currentStep={phase1Step}
            />
          </div>

          <span aria-hidden className="text-[10px]" style={{ color: `${C.gold}66` }}>›</span>

          <PhaseChip
            active={currentPhase === 2}
            done={currentPhase > 2}
            label="Création"
            icon={currentPhase > 2 ? <CheckIcon /> : phase2Icon}
          />

          <span aria-hidden className="text-[10px]" style={{ color: `${C.gold}66` }}>›</span>

          <PhaseChip
            active={currentPhase === 3}
            done={false}
            label="Finalisation"
            icon={<PackageIcon />}
          />
        </div>

        {/* Concrete sub-label — "Couverture · Bali", "Album 2 sur 2", etc. */}
        {subLabel && (
          <p className="text-[11px] font-medium" style={{ color: C.muted }}>
            {subLabel}
          </p>
        )}
      </div>

      <style>{`
        @keyframes storizMapulse {
          0%, 100% { box-shadow: 0 4px 12px ${C.gold}55; }
          50%      { box-shadow: 0 4px 16px ${C.gold}88, 0 0 0 4px ${C.gold}20; }
        }
      `}</style>
    </div>
  );
});
